import { ApiError } from '../api/client'
import type { QuotaShortfall } from '../api/types'
import { sizeNumber, sizeUnit } from '../ui/format'
import { LEASE_NAME_RULE } from './lease'
import { TIER_DEFAULT_BLOCKED, tierHeldSentence } from './quota'

// 오류는 원인을 말한다. 거부 이유(`reason`)에 문장이 있으면 그 문장이고, 없으면 백엔드 메시지가 원인을 말한다 —
// `요청 값이 올바르지 않습니다`·`지금은 처리할 수 없는 요청입니다` 같은 일반 문장은 원인을 감추므로
// 메시지가 없을 때만 쓴다. 서버 고장·연결 실패는 메시지에 사람이 할 일이 없으므로 코드의 문장이다.
const SAID: Record<ApiError['code'], string> = {
  invalid_request: '요청 값이 올바르지 않습니다.',
  unauthorized: '로그인이 필요합니다.',
  forbidden: '권한이 없습니다.',
  not_found: '찾을 수 없습니다.',
  conflict: '지금은 처리할 수 없는 요청입니다.',
  internal: '서버 오류가 발생했습니다.',
  upstream_unavailable: '장비가 응답하지 않습니다.',
  machine_unreachable: '지금은 VM 에 접속할 수 없습니다.',
  unavailable: '지금은 처리할 수 없습니다. 잠시 뒤 다시 하세요.',
  too_many_requests: '잠시 뒤 다시 하세요.',
  network: '서버에 연결할 수 없습니다.',
  unknown: '요청이 실패했습니다.',
}

// 원인을 말하지 않는 코드 문장. 이 코드의 오류는 사전에 없으면 백엔드 메시지를 그대로 보인다.
const HIDES_CAUSE: ReadonlySet<ApiError['code']> = new Set(['invalid_request', 'conflict'])

// 거부 이유 코드마다 한 문장.
const REASON: Record<string, string> = {
  // 로그인.
  login_throttled: '로그인에 여러 번 실패했습니다. 잠시 뒤 다시 하세요.',
  // 임대 신청과 VM 조작의 거절.
  owner_not_login_name: '아이디를 VM 계정 이름으로 쓸 수 없습니다. 관리자에게 문의하세요.',
  flavor_unknown: '고른 사양이 없어졌습니다. 다시 고르세요.',
  image_unknown: '고른 이미지가 없어졌습니다. 다시 고르세요.',
  node_unknown: '고른 노드가 없어졌습니다. 다시 고르세요.',
  node_pin_forbidden: '노드를 고를 수 있는 직위가 아닙니다.',
  gpu_model_unknown: '없는 GPU 모델입니다. 목록에서 다시 고르세요.',
  gpu_model_no_cards: '이 모델의 GPU 가 없습니다. 다른 모델을 고르세요.',
  gpu_pair_incomplete: 'GPU 모델과 장 수를 함께 고르세요.',
  spec_not_free: '이 사양이 정한 값은 바꿀 수 없습니다.',
  spec_missing: '이 사양에서 정해야 하는 값이 비어 있습니다.',
  foreign_filter: '다른 사람의 VM 목록은 볼 수 없습니다.',
  lease_not_yours: '다른 사람의 VM 은 보고 접속만 할 수 있습니다.',
  quota_missing: '쿼터가 정해지지 않았습니다. 관리자에게 문의하세요.',
  quota_exceeded: '쿼터가 부족합니다.',
  no_capacity: '지금은 이 사양을 받을 자리가 없습니다. 더 작은 사양을 고르거나 잠시 뒤 다시 하세요.',
  lease_unconfigured: 'VM 을 만들 이미지가 없습니다. 관리자에게 문의하세요.',
  flavor_disabled: '더 이상 신청할 수 없는 사양입니다. 다른 사양을 고르세요.',
  lease_not_pending: '이미 결정된 신청입니다.',
  machine_running: 'VM 이 이미 켜져 있습니다.',
  machine_not_running: 'VM 이 꺼져 있습니다.',
  root_disk: '기본 디스크는 바꿀 수 없습니다.',
  disk_not_attached: '연결되지 않은 디스크입니다.',
  disk_not_larger: '지금보다 큰 크기를 고르세요.',
  disk_slots_exhausted: '더 붙일 수 있는 디스크 자리가 없습니다.',
  disk_size_invalid: '고를 수 없는 디스크 크기입니다.',
  reason_missing: 'GPU 요청 사유를 적으세요.',

  // 팀.
  team_name_taken: '같은 식별자의 팀이 있습니다. 다른 식별자를 적거나 비워 두세요.',
  last_owner: '팀에는 관리자가 한 사람 이상 있어야 합니다.',
  user_not_found: '찾을 수 없는 사용자입니다.',
  user_inactive: '탈퇴했거나 비활성화된 계정은 팀에 넣을 수 없습니다.',
  // 초대(F-92). 일반 사용자인 팀 관리자는 새 사람을 바로 넣지 않고 초대한다.
  invite_required: '새 멤버는 초대로만 들어옵니다. 아이디로 초대하세요.',
  invite_admin: '관리자는 초대할 수 없습니다. 최고 관리자에게 팀에 넣어 달라고 하세요.',
  already_member: '이미 팀의 멤버입니다.',
  invitation_pending: '이미 초대해 답을 기다리는 사람입니다.',
  invitation_not_found: '찾을 수 없는 초대입니다.',
  invitation_closed: '이미 답했거나 거둔 초대입니다.',
  // 남은 수(`leases`)를 싣고 오면 `said` 가 그 수로 말한다. 서버는 승인 대기인 신청도 세므로 조치에 반려가 든다.
  team_in_use: '이 팀으로 신청한 VM 이 남아 있어 삭제할 수 없습니다. 반려·반납·파기가 끝난 뒤 삭제하세요.',

  // 이름·값이 이미 쓰이는 생성·수정의 거절.
  flavor_name_taken: '같은 이름의 사양이 있습니다. 다른 이름을 적으세요.',
  cluster_name_taken: '같은 이름의 클러스터가 있습니다. 다른 이름을 적으세요.',
  key_name_taken: '같은 이름의 키가 있습니다. 다른 이름을 적으세요.',
  key_registered: '이미 등록된 키입니다.',
  outlet_taken: '다른 노드가 쓰는 PDU 콘센트입니다.',

  // 이미지 제거의 거절.
  image_in_use: '이 이미지로 신청한 VM 이 있어 제거할 수 없습니다.',
  image_managed: '자동으로 준비되는 이미지는 제거할 수 없습니다.',

  // 사양 삭제·순서의 거절.
  flavor_order_stale: '그 사이 사양 목록이 바뀌었습니다. 다시 옮기세요.',

  // 이미 오던 거절 중 사전에 없던 것. 직위의 `in_use` 는 인원(`people`)을 싣고 와 `said` 가 따로 말한다.
  in_use: '사용 중인 GPU 모델이라 지울 수 없습니다.',
  is_default: TIER_DEFAULT_BLOCKED,
  no_cluster: '노드가 아직 클러스터에 들지 않았습니다.',
  node_has_guests: '노드에 VM 이 있어 재부팅할 수 없습니다.',
  previous_membership_pending: '이 노드의 이전 클러스터 연결을 정리하는 중입니다. 잠시 뒤 다시 하거나 다른 클러스터를 고르세요.',
  self: '자기 계정에는 할 수 없습니다.',

  retention_expired: '보존 기한이 지났습니다.',
  gpu_restore_unsupported: 'GPU VM 은 되살릴 수 없습니다.',
  node_full: '이 VM 을 되살릴 자원이 부족합니다.',
  lease_not_retired: '보존 중인 VM 에서만 할 수 있습니다.',
  name_taken: '같은 이름의 VM 을 사용 중입니다.',
  invalid_name: LEASE_NAME_RULE,

  decision_conflict: '이 노드는 이미 클러스터가 정해졌습니다.',
  node_not_awaiting_decision: '아직 클러스터를 정할 수 없습니다.',
  cluster_removed: '선택한 클러스터는 제거되었습니다.',
  cluster_not_active: '선택한 클러스터는 지금 참여를 받지 않습니다.',
  cluster_unreachable: '선택한 클러스터가 응답하지 않습니다.',
  cluster_has_workloads: '사용 중인 VM 이 있어 클러스터 기록을 지울 수 없습니다.',
  cluster_has_live_members: '이 클러스터의 노드가 아직 켜져 있습니다.',
  cluster_founding: '클러스터를 만드는 중이라 지울 수 없습니다.',
  node_not_removable: '노드가 켜져 있거나 VM 이 남아 있어 제거할 수 없습니다.',
  key_in_use: '사용 중인 VM 의 키는 지울 수 없습니다.',
  console_unavailable: '이 VM 에서는 콘솔을 쓸 수 없습니다.',
  root_password_unavailable: '이 노드에는 저장된 root 비밀번호가 없습니다.',
  node_offline: '노드가 응답하지 않습니다.',
  node_not_onboardable: '지금은 이 노드를 편입할 수 없습니다.',
  onboard_in_progress: '이미 편입이 진행 중입니다.',
  ssh_address_missing: '이 노드의 관리 주소를 알 수 없습니다.',
  not_dismissable: '이 항목은 결정이 필요합니다.',
  change_pending: '이미 기다리는 변경 신청이 있습니다.',
  change_not_pending: '이 변경은 이미 결정되었습니다.',
  nothing_to_change: '지금과 같은 숫자입니다. 바꿀 값을 적으세요.',
  disk_not_a_change: '디스크는 변경 신청이 아니라 디스크 추가로 늘립니다.',
  gpu_model_missing: 'GPU 모델을 고르세요.',
  gpu_model_not_wanted: 'GPU 를 떼는 변경에는 모델을 고를 수 없습니다.',
  // 숫자는 메모리 칸이 있는 두 창이 그 칸 아래에서 미리 말한다(`views/user/ChangeDialog.tsx`·`views/vm/ChangeApproveDialog.tsx`).
  gpu_memory_floor: '카드가 요구하는 메모리보다 적습니다. 메모리를 늘리거나 장 수를 줄이세요.',
  lease_not_active: '사용 중인 VM 에서만 할 수 있습니다.',
  machine_busy: '다른 작업이 진행 중입니다.',

  signup_closed: '지금은 계정을 신청할 수 없습니다.',
  username_taken: '이미 쓰는 아이디입니다.',
  reserved_username: 'VM 안에서 이미 쓰는 아이디입니다. 다른 아이디를 쓰세요.',
  email_taken: '이미 쓰는 이메일입니다.',
  mail_failed: '인증 메일을 보내지 못했습니다. 잠시 뒤 같은 내용으로 다시 신청하세요.',
  password_mismatch: '현재 비밀번호가 맞지 않습니다.',
  weak_password: '비밀번호는 8자 이상이어야 합니다.',
  password_required: '비밀번호를 정하세요.',
  already_withdrawn: '이미 탈퇴한 계정입니다.',
  last_superadmin: '최고 관리자가 한 사람뿐입니다. 다른 사람을 최고 관리자로 올린 뒤에 하세요.',
  lease_in_flight: '만들고 있는 VM 이 있습니다. 잠시 뒤 다시 하세요.',
  bad_token: '링크가 유효하지 않거나 만료되었습니다.',
  email_unverified: '이메일이 아직 확인되지 않아 승인할 수 없습니다.',
  mail_unavailable: '메일을 보낼 수 없어 계정을 등록할 수 없습니다.',
  bad_workbook: '엑셀 파일(.xlsx)을 고르세요.',
  bad_header: '첫 줄에 이름·아이디·이메일 열이 있어야 합니다. 양식을 내려받아 쓰세요.',
  too_large: '파일이 너무 큽니다. 나눠서 등록하세요.',
  too_many_rows: '한 번에 1000명까지 등록할 수 있습니다. 나눠서 등록하세요.',
}

// 쿼터 축마다 낱말과 값의 모양. 숫자는 그 축의 단위로 말한다 — 크기는 세 수가 모두 한 단위이고,
// **그 단위는 셋 중 0 이 아닌 가장 작은 수가 정한다.** 한도가 정하면 TB 의 소수 한 자리가 32GB 를 `<0.1TB` 로 뭉개
// 얼마나 모자라는지를 말하려는 문장이 그것을 말하지 못한다. 0 은 크기가 없어 단위를 정하지 않는다 —
// 쓴 양이 0 이라고 2TB 한도가 `2048GB` 가 되지 않는다.
type Shape = (smallest: number) => (n: number) => string
const counted = (unit: string): Shape => () => (n) => `${n}${unit}`
const sized = (toGib: (n: number) => number): Shape => (smallest) => {
  const unit = sizeUnit(toGib(smallest))
  return (n) => `${sizeNumber(toGib(n), unit)}${unit}`
}
const QUOTA_AXIS: Record<QuotaShortfall['axis'], [string, Shape]> = {
  vms: ['VM', counted('대')],
  cores: ['코어', counted('코어')],
  memory_mb: ['메모리', sized((mb) => mb / 1024)],
  disk_gb: ['디스크', sized((gb) => gb)],
}

/** 쿼터 거절의 숫자를 한 문장으로. 어느 쿼터가 얼마나 모자라는지 알아야 무엇을 줄일지 정한다. */
export function quotaSentence(q: QuotaShortfall): string {
  const [word, shape] = QUOTA_AXIS[q.axis] ?? [q.axis, () => String]
  const nonzero = [q.limit, q.used, q.asked].filter((n) => n > 0)
  const fmt = shape(nonzero.length === 0 ? 0 : Math.min(...nonzero))
  return `${word} 쿼터가 부족합니다. ${fmt(q.limit)} 중 ${fmt(q.used)} 사용 중이고 ${fmt(q.asked)} 더 필요합니다.`
}

function quotaOf(error: ApiError): QuotaShortfall | null {
  const body = error.body as { error?: { quota?: QuotaShortfall } } | null
  return body?.error?.quota ?? null
}

/** 봉투의 `people` — 사람이 든 직위를 지우려 한 거절이 싣는 인원. 다른 `in_use` 에는 없다. */
function peopleOf(error: ApiError): number | null {
  const body = error.body as { error?: { people?: number } } | null
  return body?.error?.people ?? null
}

/** 봉투의 `leases` — VM 이 남은 팀을 지우려 한 거절이 싣는 수. */
function leasesOf(error: ApiError): number | null {
  const body = error.body as { error?: { leases?: number } } | null
  return body?.error?.leases ?? null
}

/** VM 이 남은 팀은 지울 수 없다는 막힘과 할 일. 몇 대인지 알아야 무엇을 끝낼지 정한다. */
function teamHeldSentence(leases: number): string {
  return `이 팀으로 신청한 VM 이 ${leases}대 남아 있어 삭제할 수 없습니다. 반려·반납·파기가 끝난 뒤 삭제하세요.`
}

/**
 * 오류 한 문장. `reasons` 는 부르는 창이 같은 사유를 자기 조치로 말할 때의 문장이다 —
 * 사유는 같아도 창마다 할 수 있는 것이 다르다.
 */
// `codes` 는 사유 없이 오는 코드의 문장을 그 창의 뜻으로 바꾼다 — 로그인의 401 은 세션이 끝난 것이 아니라 자격증명이 틀린 것이다.
export function said(
  error: unknown,
  reasons?: Readonly<Record<string, string>>,
  codes?: Readonly<Partial<Record<ApiError['code'], string>>>,
): string {
  if (error instanceof ApiError) {
    const own = error.reason ? reasons?.[error.reason] : undefined
    if (own) return own
    if (error.reason === 'quota_exceeded') {
      const q = quotaOf(error)
      if (q) return quotaSentence(q)
    }
    if (error.reason === 'in_use') {
      const n = peopleOf(error)
      if (n !== null) return tierHeldSentence(n)
    }
    if (error.reason === 'team_in_use') {
      const n = leasesOf(error)
      if (n !== null) return teamHeldSentence(n)
    }
    const byReason = error.reason ? REASON[error.reason] : undefined
    if (byReason) return byReason
    const byCode = codes?.[error.code]
    if (byCode) return byCode
    // 사전에 없는 거절은 원인을 감추지 않는다 — 백엔드가 말한 원인이 일반 문장보다 낫다.
    const message = error.message.trim()
    if (HIDES_CAUSE.has(error.code) && message) return message
    return SAID[error.code] ?? SAID.unknown
  }
  return '오류가 발생했습니다.'
}
